import React from "react";
import {
  ArrowRight,
  BookOpen,
  Brain,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { AppCard, AppPageHeader } from "@/components/daily/CompanionAppUi";

const personalize = (value, preferredName) =>
  String(value || "")
    .replace(/the adult’s/gi, preferredName ? `${preferredName}’s` : "your")
    .replace(/the adult/gi, preferredName || "you");

const TopicDetail = ({ topic, preferredName, onBack }) => (
  <div data-testid={`companion-learning-topic-${topic.key}`}>
    <AppPageHeader
      eyebrow={topic.eyebrow}
      title={topic.title}
      body={personalize(topic.summary, preferredName)}
      onBack={onBack}
    />

    <AppCard tone="information">
      <p className="text-base leading-relaxed forr-text-soft">
        {personalize(topic.body, preferredName)}
      </p>
    </AppCard>

    {topic.points?.length > 0 && (
      <section className="mt-4 space-y-3">
        {topic.points.map((point, index) => (
          <AppCard key={point.label}>
            <div className="flex items-start gap-4">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-sm"
                style={{ backgroundColor: "var(--forr-teal-tint)", color: "var(--forr-teal-deep)" }}
              >
                {String(index + 1).padStart(2, "0")}
              </div>
              <div>
                <h2 className="font-display text-xl forr-text-ink">{point.label}</h2>
                <p className="mt-2 text-sm leading-relaxed forr-text-soft">
                  {personalize(point.body, preferredName)}
                </p>
              </div>
            </div>
          </AppCard>
        ))}
      </section>
    )}

    {topic.cards?.length > 0 && (
      <section className="mt-4 grid gap-3 sm:grid-cols-2">
        {topic.cards.map((card) => (
          <AppCard key={`${card.title}-${card.label}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl forr-text-teal-deep">{card.title}</h2>
                <div className="mt-1 text-[9px] uppercase tracking-[0.18em] forr-text-mute">
                  {card.label}
                </div>
              </div>
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl"
                style={{ backgroundColor: "var(--forr-teal-tint)", color: "var(--forr-teal-deep)" }}
              >
                <Brain size={18} />
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed forr-text-soft">
              {personalize(card.body, preferredName)}
            </p>
          </AppCard>
        ))}
      </section>
    )}
  </div>
);

const TopicButton = ({ topic, preferredName, onOpen, relevant = false }) => (
  <button
    type="button"
    onClick={onOpen}
    className="rounded-[22px] p-5 text-left forr-surface transition-transform hover:-translate-y-0.5"
    style={{
      border: `1px solid ${relevant ? "var(--forr-teal)" : "var(--forr-line-soft)"}`,
    }}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] forr-text-teal-deep">
          {topic.eyebrow}
        </div>
        <h3 className="mt-2 font-display text-xl forr-text-ink">{topic.title}</h3>
        <p className="mt-3 text-sm leading-relaxed forr-text-soft">
          {personalize(topic.summary, preferredName)}
        </p>
      </div>
      <ChevronRight size={18} className="mt-1 shrink-0 forr-text-mute" />
    </div>
  </button>
);

const CompanionLearnPage = ({ topics, activeTopicKey, preferredName, onNavigate }) => {
  const activeTopic = topics.find((topic) => topic.key === activeTopicKey);
  if (activeTopic) {
    return (
      <TopicDetail
        topic={activeTopic}
        preferredName={preferredName}
        onBack={() => onNavigate("learn")}
      />
    );
  }

  const relevant = topics.filter((topic) => topic.relevance > 1).slice(0, 5);
  const relevantKeys = new Set(relevant.map((topic) => topic.key));
  const library = topics.filter((topic) => !relevantKeys.has(topic.key));

  return (
    <div data-testid="companion-learn-page">
      <AppPageHeader
        eyebrow="Learn"
        title={preferredName ? `${preferredName}, understand what applies now` : "Understand what applies now"}
        action={<BookOpen size={24} className="forr-text-teal-deep" />}
      />

      {relevant.length > 0 && (
        <section>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="forr-overline">Relevant now</div>
              <h2 className="mt-2 font-display text-2xl forr-text-ink">Start with these topics</h2>
            </div>
            <Sparkles size={21} className="forr-text-teal-deep" />
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {relevant.map((topic) => (
              <TopicButton
                key={topic.key}
                topic={topic}
                preferredName={preferredName}
                relevant
                onOpen={() => onNavigate("learn", topic.key)}
              />
            ))}
          </div>
        </section>
      )}

      <section className="mt-7">
        <div className="forr-overline">Methodology library</div>
        <h2 className="mt-2 font-display text-2xl forr-text-ink">Explore the full framework</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {library.map((topic) => (
            <TopicButton
              key={topic.key}
              topic={topic}
              preferredName={preferredName}
              onOpen={() => onNavigate("learn", topic.key)}
            />
          ))}
        </div>
      </section>

      <button
        type="button"
        onClick={() => onNavigate("companion")}
        className="mt-7 flex w-full items-center justify-between gap-4 rounded-[24px] p-5 text-left text-white"
        style={{ background: "linear-gradient(135deg, var(--forr-teal-deep), var(--forr-teal))" }}
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">Adaptive Companion</div>
          <div className="mt-1 font-display text-xl">Ask how this applies to your current state or plan</div>
        </div>
        <ArrowRight size={19} className="shrink-0" />
      </button>
    </div>
  );
};

export default CompanionLearnPage;
